import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useFeatureFlagPayload } from "posthog-js/react";
import { PropTypes } from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import GlobalContainer from "~/components/global_container";
import {
  DEFAULT_CONTEXT_DEPTH,
  DEPTH_STORAGE_KEY,
} from "~/constants/ContextDepths";
import { NOOP_FALSE } from "~/constants/callbacks";
import {
  EDITOR_TYPES,
  FORMAT_STORAGE_KEY,
  OUTPUT_FORMATS,
} from "~/constants/EditorTypes";
import { captureEvent } from "~/helpers/capture_posthog_event";
import useAIAssist from "./hooks/useAIAssist";
import useTemplateAutoPopulation from "./hooks/useTemplateAutoPopulation";
import isFormatToggleVisible from "./lib/isFormatToggleVisible";
import { ModalStates } from "./states/ModalStates";
import TokenLimitExceededCTA from "./ui/feedback/TokenLimitExceededCTA";
import ModalContent from "./ui/ModalContent";
import ContextBadge from "./ui/toolbar/ContextBadge";
import TemplateSetIndicator from "./ui/toolbar/TemplateSetIndicator";

const AssistModal = ({
  show = false,
  onHide = NOOP_FALSE,
  editor = {},
  mode = "html",
  setSubject = NOOP_FALSE,
  editorType = null,
  leadId = null,
  product = null,
}) => {
  const [initialLimitExceeded, setInitialLimitExceeded] = useState(false);
  const [hasCheckedTokens, setHasCheckedTokens] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [promptsLoading, setPromptsLoading] = useState(false);
  const [contextDepth, setContextDepth] = useState(
    () => localStorage.getItem(DEPTH_STORAGE_KEY) || DEFAULT_CONTEXT_DEPTH,
  );
  const [outputFormat, setOutputFormat] = useState(() => {
    const editorConfig = EDITOR_TYPES[editorType];
    const stored = localStorage.getItem(FORMAT_STORAGE_KEY);
    if (stored && editorConfig?.htmlCapable) {
      return stored;
    }
    return editorConfig?.defaultOutputFormat || OUTPUT_FORMATS.TEXT;
  });
  const allowedFormatTiers = useFeatureFlagPayload(
    process.env.SHAKAPACKER_PUBLIC_AI_ASSIST_HTML_TOGGLE_ALLOWED_TIERS_FLAG,
  );

  const {
    state,
    dispatch,
    markdownContentRef,
    setTier,
    handleInputChange,
    handlePromptClick,
    handleNewPrompt,
    handleStop,
    handleClose,
    handleInsert,
    handleGenerateText,
    handleRegenerate,
    handleRetry,
    toggleVoiceInput,
    fetchTokensUsage,
  } = useAIAssist(editor, mode, setSubject, onHide, {
    product,
    editorType,
    outputFormat,
    contextDepth,
    leadId,
  });

  // BK-3229 R4 — template auto-population lives at modal scope so the
  // Header can render a single TemplateSetIndicator next to ContextBadge
  // instead of one icon per selector. `manualChangeHandlers` wrap the raw
  // setters to clear template flags on direct user interaction.
  const {
    templateFlags,
    activeTemplate,
    applyTemplateDefaults,
    manualChangeHandlers,
  } = useTemplateAutoPopulation({
    onTierChange: setTier,
    onContextDepthChange: setContextDepth,
    onOutputFormatChange: setOutputFormat,
  });

  const isTemplateApplied = !!(
    templateFlags?.model ||
    templateFlags?.context ||
    templateFlags?.format
  );

  useEffect(() => {
    (async () => {
      await fetchTokensUsage();
      setHasCheckedTokens(true);
    })();
  }, [fetchTokensUsage]);

  useEffect(() => {
    if (show) {
      // PRD AC 3.1.1: category resets to "All" each time the modal opens.
      setSelectedCategory(null);
      captureEvent("ai_assist_modal_opened", {
        editor_type: editorType,
        module: product,
        channel: EDITOR_TYPES[editorType]?.channel,
        has_contact_context: !!leadId,
        contact_id: leadId || undefined,
        output_format: outputFormat,
      });
    }
  }, [show]);

  const showFormatToggle = isFormatToggleVisible(
    editorType,
    mode,
    state.tier,
    allowedFormatTiers,
  );

  // Reset to text only when toggle transitions from visible → hidden (tier switch),
  // not on initial render when PostHog hasn't loaded yet.
  const prevShowFormatToggle = useRef(showFormatToggle);
  useEffect(() => {
    if (
      prevShowFormatToggle.current &&
      !showFormatToggle &&
      outputFormat !== OUTPUT_FORMATS.TEXT
    ) {
      setOutputFormat(OUTPUT_FORMATS.TEXT);
    }
    prevShowFormatToggle.current = showFormatToggle;
  }, [showFormatToggle]);

  // Set initial limit check ONCE after first fetch completes
  useEffect(() => {
    if (hasCheckedTokens && !initialLimitExceeded) {
      const exceeded =
        state.subscriptionLimit !== null &&
        state.totalTokensUsed >= state.subscriptionLimit;
      if (exceeded) {
        setInitialLimitExceeded(true);
      }
    }
  }, [hasCheckedTokens]);

  const isGenerating = state.status === ModalStates.GENERATING;

  const ModalHeader = (text) => (
    <div className="modal-header">
      <h5 className="modal-title my-auto" id="bkAIGenerator">
        {text}
        {!initialLimitExceeded && (
          <>
            <ContextBadge product={product} editorType={editorType} />
            <TemplateSetIndicator
              isVisible={isTemplateApplied}
              templateTitle={activeTemplate?.title}
              rationale={activeTemplate?.rationale}
            />
          </>
        )}
      </h5>

      <button
        type="button"
        className="close mx-0 my-auto p0"
        onClick={handleClose}
        aria-label="Close"
      >
        <span aria-hidden="true">&times;</span>
      </button>
    </div>
  );

  return (
    <Modal
      show={show}
      onHide={handleClose}
      backdrop="static"
      enforceFocus={false}
      size={initialLimitExceeded ? undefined : "lg"}
      className={`ai-assist-modal-overlay ${GlobalContainer.product()}`}
      backdropClassName="ai-assist-modal-backdrop"
    >
      {ModalHeader(
        initialLimitExceeded
          ? "Upgrade Now For More AI Tokens! 🚀"
          : "AI Assist",
      )}

      {(!hasCheckedTokens || state.isLoading) && (
        <div className="modal-body">
          <div className="text-center">
            <FontAwesomeIcon icon="far fa-spinner" pulse size="lg" />
          </div>
        </div>
      )}

      {hasCheckedTokens && !initialLimitExceeded && (
        <ModalContent
          state={state}
          dispatch={dispatch}
          markdownContentRef={markdownContentRef}
          handleInputChange={handleInputChange}
          handlePromptClick={handlePromptClick}
          handleGenerateText={handleGenerateText}
          handleStop={handleStop}
          handleInsert={handleInsert}
          handleRegenerate={handleRegenerate}
          handleNewPrompt={handleNewPrompt}
          handleRetry={handleRetry}
          selectedCategory={selectedCategory}
          onSelectedCategoryChange={setSelectedCategory}
          onPromptsLoadingChange={setPromptsLoading}
          selectedTier={state.tier}
          onTierChange={manualChangeHandlers.tier}
          leadId={leadId}
          editorType={editorType}
          product={product}
          mode={mode}
          contextDepth={contextDepth}
          onContextDepthChange={manualChangeHandlers.contextDepth}
          outputFormat={outputFormat}
          onOutputFormatChange={manualChangeHandlers.outputFormat}
          showFormatToggle={showFormatToggle}
          toggleVoiceInput={toggleVoiceInput}
          templateFlags={templateFlags}
          applyTemplateDefaults={applyTemplateDefaults}
        />
      )}

      {hasCheckedTokens && initialLimitExceeded && (
        <TokenLimitExceededCTA state={state} />
      )}
    </Modal>
  );
};

AssistModal.propTypes = {
  show: PropTypes.bool,
  onHide: PropTypes.func,
  editor: PropTypes.shape({}),
  mode: PropTypes.string,
  setSubject: PropTypes.func,
  editorType: PropTypes.string,
  leadId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  product: PropTypes.string,
};

export default AssistModal;
